import React from 'react';
import { Phone, Clock, MapPin } from 'lucide-react';

const TarangireOneDay = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white text-[#2f4f2f] py-4 px-6 border-b border-[#9acd32]/20">
        <h1 className="text-2xl font-bold">1 DAY TOUR TO TARANGIRE NATIONAL PARK</h1>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <h2 className="text-3xl font-bold text-[#2f4f2f] mb-6">
          1 Day Tour To Tarangire National Park – Tarangire Day Trip from Arusha
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Main Content - Left Side */}
          <div className="md:col-span-2">
            {/* Intro */}
            <div className="mb-8">
              <p className="text-gray-700 leading-relaxed mb-4">
                Experience the thrill of a <strong>1 Day Tour to Tarangire National Park</strong>, located approximately 118km from Arusha. This immersive African safari begins with an early morning pickup from your hotel, offering the chance to see large herds of elephants and a variety of wildlife including zebras, wildebeest, lions, rhinos, hyenas, cheetahs, giraffes, baboons, and warthogs, all set against the park’s iconic baobab trees and expansive landscapes.
              </p>
            </div>

            {/* Placeholder for Safari Image */}
            <div className="mb-8">
              <div className="bg-[#f5f5f5] h-64 rounded-lg flex items-center justify-center text-gray-500 text-sm">
                <img 
                src="/ELEPHANT.JPG" 
                alt="3 Days Tanzania Wildlife Safari" 
                className="w-full h-48 object-cover"
              />
                
              </div>
              <p className="text-center text-xs text-gray-500 mt-2">
                Tarangire National Park - Elephant with baby
              </p>
            </div>

            {/* Itinerary */}
            <h3 className="text-2xl font-bold text-[#2f4f2f] mb-6">
              Itinerary for the 1 Day Tour to Tarangire National Park
            </h3>

            <div className="space-y-6">
              <div className="p-4 border border-[#9acd32]/20 rounded-lg">
                <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 1: Tarangire National Park Day Trip</h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  Your day begins with a meet-and-greet with your driver guide at your hotel in Arusha, followed by a scenic 2-hour drive to Tarangire National Park. Known for its large elephant herds, sometimes numbering up to 300 near the Tarangire River, the park features open savannah grasslands and iconic baobab trees. During your game drive, expect sightings of giraffes, wildebeest, zebras, antelopes, and predators like lions and cheetahs, along with vibrant birdlife. Enjoy a picnic lunch at a riverside site, followed by additional game viewing before returning to Arusha by late afternoon.
                </p>
              </div>
            </div>

            {/* Inclusions & Exclusions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
              <div>
                <h3 className="text-lg font-bold text-[#2f4f2f] mb-2">Inclusions</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Transportation via 4x4 safari vehicle with a pop-up roof
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Park entry fees
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Game drives as per itinerary
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Lunch & bottled water
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Professional English-speaking driver guide
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-bold text-[#2f4f2f] mb-2">Exclusions</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Visa fees & flights
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Tips for guides
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Travel insurance
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Optional activities
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Items of personal nature
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="md:col-span-1">
            <div className="sticky top-6">
              <div className="bg-white border border-[#9acd32]/20 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-bold text-[#2f4f2f] mb-2">1 Day Tarangire Tour</h3>
                <ul className="text-sm text-gray-700 mb-4 space-y-2">
                  <li className="flex items-center">
                    <Clock className="mr-2 h-4 w-4 text-gray-600" />
                    Duration: 1 Day, Daily safari
                  </li>
                  <li className="flex items-center">
                    <MapPin className="mr-2 h-4 w-4 text-gray-600" />
                    Regions / Arusha
                  </li>
                  <li className="text-xs text-gray-600 ml-6">
                    Hotel pickup - Your side returned to your hotel
                  </li>
                </ul>
                <button className="w-full bg-[#2f4f2f] hover:bg-[#228b22] text-white font-semibold py-3 px-4 rounded-lg mb-4 transition-colors">
                  Plan Your Trip Now
                </button>
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-2">Call/WhatsApp for a Free Quote</p>
                  <p className="text-lg font-bold text-[#2f4f2f]">
                    <Phone className="inline mr-2" size={20} />
                    <a href="tel:+255753182737" className="text-[#9acd32] hover:text-[#228b22] transition-colors">+255 753 182 737</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TarangireOneDay;